/**
 * /accept-invite — where an invitation link lands.
 *
 * HOW THE LINK WORKS
 * Supabase's invite link carries tokens in the URL. The client is configured
 * with detectSessionInUrl: true, so the SDK exchanges them for a session before
 * this component settles. That means arriving here signed-in is the SUCCESS
 * case, not a bug.
 *
 * If no session appears, the link was expired, already used, or the redirect
 * was not on Supabase's allow-list. Say so plainly — a blank screen at this
 * moment is the worst possible first impression of the product.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../auth/useAuth.js';
import Field from '../components/Field.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';

export function AcceptInvitePage() {
  const { authStatus, user } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Give the SDK a moment to consume the tokens in the URL before deciding the
  // link was bad.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (authStatus === 'loading' || (authStatus === 'signed-out' && !settled)) {
    return (
      <div className="centered-page" aria-busy="true">
        <p className="muted">Checking your invitation…</p>
      </div>
    );
  }

  if (authStatus === 'signed-out') {
    return (
      <div className="centered-page">
        <div className="card">
          <h1>This invitation link didn’t work</h1>
          <p>
            Invitation links can only be used once, and they expire after 24
            hours. This one has been used already, has expired, or was copied
            incompletely.
          </p>
          <p>
            Ask whoever invited you to send a new one. If you have already set a
            password, sign in instead.
          </p>
          <button type="button" className="btn btn--primary" onClick={() => navigate('/login')}>
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);

    if (password.length < 10) {
      setError('Please use at least 10 characters. Longer is better than complicated.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setBusy(false);
      return;
    }
    navigate('/', { replace: true });
  }

  return (
    <div className="centered-page">
      <form className="card" onSubmit={handleSubmit}>
        <h1>Choose your password</h1>
        <p className="muted">
          Setting up the account for <strong>{user?.email}</strong>. Only you will
          ever know this password.
        </p>

        <ErrorBanner error={error} />

        <Field
          label="New password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          hint="At least 10 characters. A short phrase you can remember beats a scrambled word."
          disabled={busy}
        />
        <Field
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          disabled={busy}
        />

        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save password and continue'}
        </button>
      </form>
    </div>
  );
}

export default AcceptInvitePage;
